import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import axios from 'axios';
import { Form } from 'react-formio';
import "../css/FormioOverrides.css";
import "../css/UserFormHeader.css";
import "../css/UserFormView.css";
import { ensurePlainObject, formatPhoneDisplay } from '../../utils/formUtils';
import {
  buildInitialFormData,
  ensureDatagridCellLabels,
  filterPersonDataForFormSave,
  preserveParticipantIdentityFromPerson,
  sanitizeFormioSelectDefaults,
  sanitizeSelectSubmissionValues,
} from '../../utils/formSchemaUtils';
import {
  normalizeAccreditationPhotoForFormio,
  getAccreditationPhotoDisplayUrls,
} from '../../utils/emsPhotoUtils';
import { configureAccreditationFormFileUploads } from '../../constants/accreditationUploads';
import {
  loadSavedProgress,
  setFormProgressData,
  updateLocalPanelProgress,
} from '../../services/PanelDataService';
import {
  calculateVisibleFieldProgress,
  isComponentVisible,
  isFormValueFilled,
} from '../../utils/formVisibleProgress';
import AccreditationPhotoManager from './AccreditationPhotoManager';
import {
  REGISTRATION_FORM_ID,
  formSchemaHasCaddieSelect,
  fetchCaddieOptions,
  buildCaddiesById,
  injectCaddieSelectValues,
  reinjectCaddieOptionsOnFormio,
  ensureCaddieDatagridLayoutFromSchema,
  applyCaddieOnFormChange,
  syncCaddieFieldsForFormio,
  ensureDatagridRowActions,
  normalizeDataGridForFormioRender,
  copyCaddieFieldsFromSource,
  personDataHasCaddieFields,
  preserveAndNormalizeCaddiesOnSave,
  prepareCaddieGridDisplayData,
  pushFormioSubmissionData,
  isDefaultRadioFormChange,
  resolveFormioWebform,
  enforceExclusiveDefaultRadioDom,
  getCaddieDatagridLayout,
  mergeKnownCaddiesIntoOptionsList,
  attachCaddieEmailAutoFill,
} from '../../utils/caddieFormioUtils';
import { USER_PORTAL_FORMIO_OPTIONS } from '../../utils/formioFormOptions';
import { formNameToCssClass } from '../../utils/formioFormNameClass';
import mergeWith from 'lodash/mergeWith';
import cloneDeep from 'lodash/cloneDeep';
import { snapshotFormioInstanceData } from '../../utils/snapshotFormioInstanceData';
import UserFormPrototypeHeader, { FORM_TABS, getNextFormTab } from './UserFormPrototypeHeader';
import UnsavedChangesModal from './UnsavedChangesModal';
import { forceFormioShowValidationErrors } from '../../utils/formioSubmissionNormalize';


// --- CONFIGURATION ---
const FORM_MAPPING = {
  registration: '68c7eefbd44999eedb541043',
  accreditation: '68e6505866f25479291b933f',
  travel: '68e781408e1384b2c283c3af'.trim(),
  accommodation: '68e784018e1384b2c283c3bb',
  guest: '68e785cb8e1384b2c283c3c5',
};
const ATHLETE_GROUP_ID = "6884aa884fb3247b61564ae5";

/** Map participant_guests record into Personal Registration form submission data. */
const buildGuestDataForPersonalForm = (guest, segment, groupId = '', groupName = '') => {
  const formData = guest?.form_data && typeof guest.form_data === 'object' ? guest.form_data : {};
  const upper = String(segment || '').toUpperCase();
  const lower = String(segment || '').toLowerCase();
  const tabData = {
    ...(formData[upper] && typeof formData[upper] === 'object' ? formData[upper] : {}),
    ...(formData[lower] && typeof formData[lower] === 'object' ? formData[lower] : {}),
  };
  const meta = guest?.meta && typeof guest.meta === 'object' && !Array.isArray(guest.meta)
    ? guest.meta
    : {};

  const base = {
    ...meta,
    ...tabData,
    Group_id: groupId,
    groupName: groupName,
  };

  if (lower === 'registration') {
    if (!base.givenFirstName) base.givenFirstName = guest?.firstName || meta.givenFirstName || '';
    if (!base.familyLastName) base.familyLastName = guest?.lastName || meta.familyLastName || '';
    if (!base.emailAddress) base.emailAddress = guest?.email || meta.emailAddress || '';
    if (!base.fullNameForScoreBoard && (base.givenFirstName || guest?.firstName)) {
      base.fullNameForScoreBoard = base.givenFirstName || guest.firstName;
    }

    // Guest meta uses phoneNumber container; Registration form uses phoneGroup2.
    const phoneSrc =
      (base.phoneNumber && typeof base.phoneNumber === 'object' ? base.phoneNumber : null) ||
      (meta.phoneNumber && typeof meta.phoneNumber === 'object' ? meta.phoneNumber : null);
    if (phoneSrc && !base.phoneGroup2) {
      base.phoneGroup2 = {
        country: phoneSrc.country || undefined,
        phoneNumber1: phoneSrc.phoneNumber1 || formatPhoneDisplay(phoneSrc) || '',
      };
    }
    delete base.phoneNumber;
  }

  return base;
};



const safeForEachRow = (rows, cb) => {
  if (!rows || !Array.isArray(rows)) return;
  rows.forEach(row => {
    if (Array.isArray(row)) row.forEach(cell => cb(cell));
    else if (row && typeof row === 'object') cb(row);
  });
};

const traverseComponents = (components, handler) => {
  if (!components || !Array.isArray(components)) return;
  components.forEach(component => {
    handler(component);
    if (component.components) traverseComponents(component.components, handler);
    if (component.columns) component.columns.forEach(col => traverseComponents(col.components || [], handler));
    if (component.rows) safeForEachRow(component.rows, cell => cell?.components && traverseComponents(cell.components, handler));
    if (component.datagrid?.components) traverseComponents(component.datagrid.components, handler);
    if (component.editgrid?.components) traverseComponents(component.editgrid.components, handler);
    // Handle tabs component
    if (component.type === 'tabs' && Array.isArray(component.components)) {
      component.components.forEach(tab => {
        if (tab?.components) traverseComponents(tab.components, handler);
      });
    }
  });
};
/** Safe calculateValue for phone fields: phoneGroup may be undefined or use different property names. */
const SAFE_PHONE_CALCULATE =
  "var pg = data.phoneGroup; if (!pg) return ''; " +
  "var code = (pg.country && typeof pg.country === 'object' && pg.country.dial_code) ? pg.country.dial_code : (pg.countryCode || ''); " +
  "var digits = pg.phoneNumberDigits || pg.phoneNumber1 || pg.phoneNumber || ''; " +
  "return (code && digits) ? code + digits : '';";

async function isReachable(url, timeoutMs = 3000) {
  if (!url) return false;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    // redirect:'manual' — treat 302 to S3 as "exists" without a cross-origin follow (CORS).
    const head = await fetch(url, {
      method: 'HEAD',
      redirect: 'manual',
      signal: controller.signal,
    });
    if (head.status >= 200 && head.status < 400) return true;
    if (head.status === 404 || head.status === 410) return false;
    if (head.status === 405) {
      const get = await fetch(url, {
        method: 'GET',
        redirect: 'manual',
        signal: controller.signal,
      });
      if (get.status >= 200 && get.status < 400) return true;
      if (get.status === 404 || get.status === 410) return false;
    }
    // Ambiguous (401/403/5xx) — do not treat as missing.
    return true;
  } catch (_) {
    // Network/CORS/abort — keep the URL; dropping valid S3 photos is worse.
    return true;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Prevent Formio runtime errors when stored photo files were deleted/moved.
 * Keep preview bound to the current photo URL value.
 */
async function ensureAccreditationPhotoDisplay(photoVal, API_URL) {
  const normalized = normalizeAccreditationPhotoForFormio(photoVal, API_URL);
  if (!Array.isArray(normalized)) return normalized;

  const out = [];
  for (const entry of normalized) {
    if (!entry || typeof entry !== 'object') {
      out.push(entry);
      continue;
    }

    // Always prefer the current URL (latest saved/cropped image).
    // Do not switch to original_url so all screens show the same property.
    let chosen = '';
    if (entry.url) {
      // eslint-disable-next-line no-await-in-loop
      const ok = await isReachable(entry.url);
      // Only drop on a definitive same-origin 404; keep on CORS/redirect/network ambiguity.
      if (ok) chosen = entry.url;
    }

    // IMPORTANT: don't inject '/images/avatar.png' into Formio file component value.
    // Formio may treat it like an uploaded file and issue DELETE on remove, causing runtime overlay.
    if (!chosen) {
      continue;
    }

    out.push({
      ...entry,
      url: chosen,
      name: entry.name || String(chosen).split('/').pop() || 'photo.png',
      type: entry.type || 'image/png',
      storage: 'url',
    });
  }
  return out;
}

const ensureSafePhoneGroupCalculate = (components) => {
  if (!Array.isArray(components)) return;
  traverseComponents(components, (comp) => {
    const calc = (comp.calculateValue || "").trim();
    if (
      calc &&
      calc.includes("phoneGroup.countryCode") &&
      (comp.key === "fullPhoneNumber" ||
        comp.key === "fullPhoneNumber1" ||
        comp.key === "professionalPhoneNumber" ||
        calc.includes("phoneNumberDigits"))
    ) {
      comp.calculateValue = SAFE_PHONE_CALCULATE;
    }
  });
};

const overrideSubmitSettings = (components) => {
  traverseComponents(components, (component) => {
    if (component.key === 'submit' && component.type === 'button') {
      component.label = 'Save & Continue';
      component.disableOnInvalid = false;
      component.disabled = false;
      component.hidden = component.hidden || false;
      if (typeof component.hideOnInvalid !== 'undefined') component.hideOnInvalid = false;
      if (typeof component.hideOnSubmit !== 'undefined') component.hideOnSubmit = false;
    }
  });
};

/**
 * Form.io only clears the submit-button spinner on `submitDone` / `submitError`.
 * Local portal saves resolve with saved=false, so Guest (last tab, no navigate)
 * leaves the button stuck loading. Reset spinner + disabled after our API save.
 *
 * Do NOT emit submitDone/submitError — those inject buttonMessage text
 * ("Please check the form...") next to Save & Continue and make the button jump.
 */
const clearFormioSubmitLoading = (formio) => {
  if (!formio) return;
  try {
    formio.loading = false;
    formio.submitting = false;
    if (typeof formio.everyComponent !== 'function') return;
    formio.everyComponent((comp) => {
      const c = comp?.component || {};
      if (c.type !== 'button') return;
      if (c.action !== 'submit' && c.key !== 'submit') return;
      comp.loading = false;
      comp.disabled = false;
      comp.hasError = false;
      if (comp.refs?.button) {
        comp.setDisabled?.(comp.refs.button, false);
        comp.removeClass?.(comp.refs.button, 'btn-danger submit-fail');
        comp.removeClass?.(comp.refs.button, 'btn-success submit-success');
      }
      if (comp.refs?.buttonMessageContainer) {
        comp.removeClass?.(comp.refs.buttonMessageContainer, 'has-error');
        comp.removeClass?.(comp.refs.buttonMessageContainer, 'has-success');
      }
      if (comp.refs?.buttonMessage) {
        comp.setContent?.(comp.refs.buttonMessage, '');
      }
    });
  } catch (err) {
    console.warn('clearFormioSubmitLoading failed', err);
  }
};

/**
 * stripRequiredUI
 * ----------------
 * PURPOSE:
 *   - Do NOT touch validation
 *   - ONLY hide the visual asterisk in labels
 *   - Preserve Form.io required-field behavior fully
 */
const stripRequiredUI = (components) => {
  traverseComponents(components, (c) => {
    if (!c) return;

    if (c.label) {
      // Some schemas store labels like "Check-in Date * " (trailing spaces after *).
      // Remove any trailing required asterisks + surrounding whitespace so Formio shows only one required marker.
      c.label = c.label.replace(/\s*\*+\s*$/, '').trim();
    }
  });
};

/** True when every visible required field has a value (optional fields ignored). */
const areRequiredFieldsFilled = (data = {}, components = [], formInstance = null) => {
  const requiredKeys = [];

  if (formInstance?.everyComponent) {
    formInstance.everyComponent((inst) => {
      const c = inst.component || {};
      if (!c.input || !c.key) return;
      if (c.type === 'button' || c.type === 'hidden') return;
      if (c.hidden === true || inst.visible === false) return;
      if (c.validate?.required) requiredKeys.push(c.key);
    });
  } else {
    const walk = (comps) => {
      if (!comps) return;
      comps.forEach((c) => {
        if (!c || !isComponentVisible(c, data)) return;
        if (
          c.input &&
          c.key &&
          c.type !== 'button' &&
          c.type !== 'hidden' &&
          c.validate?.required
        ) {
          requiredKeys.push(c.key);
        }
        if (c.components) walk(c.components);
        if (c.columns) c.columns.forEach((col) => walk(col.components));
        if (c.rows) safeForEachRow(c.rows, (cell) => cell?.components && walk(cell.components));
        if (c.datagrid?.components) walk(c.datagrid.components);
        if (c.editgrid?.components) walk(c.editgrid.components);
      });
    };
    walk(components);
  }

  const unique = [...new Set(requiredKeys)];
  if (!unique.length) return true;
  return unique.every((key) => isFormValueFilled(data[key]));
};

/** Visible UI fields only (skips hidden / conditional fields). */
const calculateProgress = (data = {}, components = [], formInstance = null) =>
  calculateVisibleFieldProgress(data, components, formInstance);

// modifySchemaForProgress (unchanged)
const modifySchemaForProgress = (components, panelProgress) => {
  if (!components || !Array.isArray(components)) return components;
  const cloned = structuredClone(components);


  const traverseAndModify = (comps) => {
    if (!comps) return;
    comps.forEach(component => {
      const isPanel = component.type === 'panel' || component.type === 'fieldset';

      if (isPanel && component.key && component.components) {
        const progress = panelProgress[component.key] || { filled: 0, total: 0, percentage: 0 };

        if (progress.total > 0) {
          const originalTitle = component.title ? component.title.replace(/<span class="formio-progress-badge.*?>.*?<\/span>/g, '').trim() : component.title || component.label || '';
          const badgeText = `${progress.filled}/${progress.total}`;
          const badgeClass = progress.percentage === 100 ? 'badge-complete' : 'badge-progress';
          const badgeColor = progress.percentage === 100 ? '#198754' : '#C9932E';
          const badgeHtml = `<span class="formio-progress-badge ${badgeClass}" aria-hidden="true" style="--progress:${progress.percentage}%;--progress-color:${badgeColor};">${badgeText}</span>`;
          component.title = `${originalTitle} ${badgeHtml}`.trim();
        }

        traverseAndModify(component.components);
      } else if (component.components) {
        traverseAndModify(component.components);
      } else if (component.columns) {
        component.columns.forEach(col => traverseAndModify(col.components));
      } else if (component.rows) {
        safeForEachRow(component.rows, cell => cell?.components && traverseAndModify(cell.components));
      } else if (component.datagrid?.components) {
        traverseAndModify(component.datagrid.components);
      } else if (component.editgrid?.components) {
        traverseAndModify(component.editgrid.components);
      }
    });
  };

  traverseAndModify(cloned);
  return cloned;
};

const updateFormioBadges = (formInstance, panelProgress) => {
  if (!formInstance) return;
  try {
    formInstance.everyComponent((compInstance) => {
      const schema = compInstance.component || {};
      const isPanel = schema.type === 'panel' || schema.type === 'fieldset';
      if (!isPanel || !schema.key) return;

      const progress = panelProgress[schema.key];
      if (!progress || progress.total === 0) return;

      const { percentage, filled, total } = progress;
      const badgeText = `${filled}/${total}`;
      const badgeClass = percentage === 100 ? 'badge-complete' : 'badge-progress';
      const badgeColor = percentage === 100 ? '#198754' : '#C9932E';

      const headerElement = 
        compInstance.element &&
        (compInstance.element.querySelector('.card-header') ||
         compInstance.element.querySelector('.panel-heading') ||
         compInstance.element.querySelector('legend'));

      if (!headerElement) return;

      headerElement.querySelectorAll('.formio-progress-badge').forEach(n => n.remove());

      const badgeEl = document.createElement('span');
      badgeEl.textContent = badgeText;
      badgeEl.className = `formio-progress-badge ${badgeClass}`;
      badgeEl.style.setProperty('--progress', `${percentage}%`);
      badgeEl.style.setProperty('--progress-color', badgeColor);

      headerElement.appendChild(badgeEl);
    });
  } catch (err) {
    console.warn('updateFormioBadges error', err);
  }
};

/**
 * Attaches a confirmation alert to the Guest form's delete icon in the DataGrid/EditGrid.
 * Includes console logging to help debug if the event is firing.
 * @param {object} formioInstance - The Form.io instance.
 * @param {string} normalizedSegment - The current form segment (e.g., 'guest').
 */
const attachDeleteConfirmation = (formioInstance, normalizedSegment) => {
    // Only apply this confirmation logic to the Guest form
    if (normalizedSegment !== 'guest') return;

    console.log("Attaching delete confirmation logic to the 'guest' form...");

    // Listen for the internal 'removeRow' event, which fires when a delete icon is clicked 
    // in an EditGrid or DataGrid.
    // The 'true' argument ensures this listener is executed BEFORE the default row removal action.
    formioInstance.on('removeRow', (component, data, index) => {
        // Log to confirm the event fired and check the component key
        console.log("removeRow event fired. Component key:", component.key); 

        // The component passed here is the parent grid component (e.g., 'guestDetails').
        // We ensure we are only targeting the specific Guest Details grid component key.
        if (component.key === 'guestDetails') { 
            const guestIndex = index + 1;
            const confirmationMessage = `Are you sure you want to delete guest row #${guestIndex}? This action cannot be undone.`;
            
            // Show the native confirmation dialog
            if (!window.confirm(confirmationMessage)) {
                console.log("Deletion cancelled by user.");
                // Returning false cancels the default 'removeRow' action.
                return false; 
            }
            console.log("Deletion confirmed by user. Proceeding with row removal.");
            // If true is returned, the default action proceeds, and the row is removed.
        }
    }, true); 
};











const UserFormView = () => {
  const { pathSegment, id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Guest mode only when opened from Guest Registration list (?guestId=…).
  // Sidebar Personal Registration has no query → logged-in user details.
  const editingGuestId = (searchParams.get('guestId') || '').trim();
  const editingGuestParticipantId = (searchParams.get('hostParticipantId') || '').trim();
  const isEditingGuest = Boolean(editingGuestId && editingGuestParticipantId);
  const guestQuery = isEditingGuest
    ? `?guestId=${encodeURIComponent(editingGuestId)}&hostParticipantId=${encodeURIComponent(editingGuestParticipantId)}`
    : '';
  const formViewPath = (tabKey, tabId) =>
    `/user/form/view/${tabKey}/${tabId}${guestQuery}`;
  const normalizedSegment = pathSegment?.toLowerCase();
  // const groupName = localStorage.getItem("groupName");

// Forms to hide completely for Athlete
// Forms to hide completely for Athlete
  const [participantRecordId, setParticipantRecordId] = useState(null);
  const [submissionData, setSubmissionData] = useState({});
  const HIDDEN_FOR_ATHLETE = ["travel", "accommodation"];

// 💡 IMPORTANT: HIDDEN_FOR_ATHLETE_FORM_IDS is not strictly needed here as we use pathSegment (normalizedSegment)
// to check the form type, but keeping it commented for reference or if ID check is preferred.

  const isAthlete = submissionData?.Group_id === ATHLETE_GROUP_ID;

  const isHiddenForAthlete = useMemo(() => {
    // Check 1: Must be an athlete
    if (!isAthlete) return false;

    // Check 2: Must be one of the hidden segments
    return normalizedSegment && HIDDEN_FOR_ATHLETE.includes(normalizedSegment);
  }, [isAthlete, normalizedSegment]);

  const targetFormId = FORM_MAPPING[normalizedSegment] || id;

  const personId = localStorage.getItem('personId');

  const originalFormSchemaRef = useRef(null);
  /** Form.io webform — same pattern as FormioTabRenderer (react-formio ref + onReady). */
  const reactFormRef = useRef(null);
  const formioWebformRef = useRef(null);
  const formRef = useRef(null);

  const [formSchema, setFormSchema] = useState(null);
  const [formNameClass, setFormNameClass] = useState('');
  const [portalSummary, setPortalSummary] = useState({ participant: null, person: null });
  const [initialSubmissionData, setInitialSubmissionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  /** Reveal inline field errors only after a failed submit (not on load). */
  const [showInlineErrors, setShowInlineErrors] = useState(false);
  /** True only after a full save (or when loaded data is already complete). */
  const [formFullySaved, setFormFullySaved] = useState(false);
  /** True after user edits since last load/save — drives Unsaved changes modal. */
  const [hasUnsavedEdits, setHasUnsavedEdits] = useState(false);
  const hasUnsavedEditsRef = useRef(false);
  const [tabGuardMessage, setTabGuardMessage] = useState('');
  const [leaveTabConfirm, setLeaveTabConfirm] = useState(null);
  const [leaveTabSaving, setLeaveTabSaving] = useState(false);
  /** After modal "Save and next", navigate here once save succeeds. */
  const pendingNavigateTabRef = useRef(null);
  /** Ignore Form.io hydrate changes so a complete form stays "saved". */
  const allowDirtyTrackingRef = useRef(false);
  /** JSON snapshot after hydrate — mid-edit = data differs from this. */
  const pristineDataRef = useRef(null);
  /** Timer that enables dirty tracking after Form.io hydrate. */
  const dirtyEnableTimerRef = useRef(null);
  /** Cleanup for DOM mid-edit listeners (input/change on form wrapper). */
  const dirtyDomCleanupRef = useRef(null);
  /** Incremented after a user crops their photo; forces the Formio form to remount with the new URL. */
  const [photoKey, setPhotoKey] = useState(0);
  const panelProgressRef = useRef({});
  /** Ref forwarded to AccreditationPhotoManager to trigger crop/view from the portal button. */
  const photoManagerRef = useRef(null);
  /** DOM node of the Formio photo field — portal target for the inline Edit/Crop button. */
  const [photoFieldPortalEl, setPhotoFieldPortalEl] = useState(null);
  const formWrapperRef = useRef(null);
  const caddiesByIdRef = useRef(new Map());
  const hasCaddieSelectRef = useRef(false);
  /** Last known server person_data — merged on save so admin edits in other tabs are preserved. */
  const personDataBaselineRef = useRef({});
  /** Logged-in CRM person — identity must not be replaced by default caddie fields. */
  const personRecordRef = useRef(null);
  /** When editing a guest in Personal Registration, hold their form_data + record. */
  const guestFormDataRef = useRef({});
  const guestRecordRef = useRef(null);
  const isSubmittingRef = useRef(false);
  const API_URL =
    process.env.REACT_APP_API_URL ||
    (process.env.NODE_ENV === 'development' ? 'http://localhost:5000/api' : '/api');

// Removed redundant participantGroupId variable

const updateProgressStats = useCallback((data, components, initialLoad = true) => {
    const formSegmentKey = normalizedSegment;

    if (!components || !formSegmentKey) {
      return { newPanelProgress: {}, globalProgressStats: { percentage: 0, filled: 0, total: 0 } };
    }

    const formInstance = formRef.current || null;
    const globalProgressResult = calculateProgress(data, components, formInstance);
    const newPanelProgress = { ...(globalProgressResult.byPanel || {}) };

    if (formRef.current) {
      updateFormioBadges(formRef.current, newPanelProgress);
    }

    panelProgressRef.current = newPanelProgress;

    if (initialLoad && originalFormSchemaRef.current) {
      const schemaClone = JSON.parse(JSON.stringify(originalFormSchemaRef.current));
      schemaClone.components = modifySchemaForProgress(schemaClone.components, newPanelProgress);
      return { schema: schemaClone, globalProgressStats: globalProgressResult };
    }

    return { globalProgressStats: globalProgressResult };
}, [normalizedSegment]);


  useEffect(() => {
    const fetchFormAndUser = async () => {
      if (!personId) {
        setError('User not logged in.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setFormSchema(null); setFormNameClass(''); setInitialSubmissionData(null);
      setPortalSummary({ participant: null, person: null });
      setShowInlineErrors(false);
      setFormFullySaved(false);
      setHasUnsavedEdits(false);
      hasUnsavedEditsRef.current = false;
      setTabGuardMessage('');
      allowDirtyTrackingRef.current = false;
      pristineDataRef.current = null;
      if (dirtyEnableTimerRef.current) {
        clearTimeout(dirtyEnableTimerRef.current);
        dirtyEnableTimerRef.current = null;
      }
      if (dirtyDomCleanupRef.current) {
        dirtyDomCleanupRef.current();
        dirtyDomCleanupRef.current = null;
      }
      originalFormSchemaRef.current = null;
      formRef.current = null;
      formioWebformRef.current = null;
      panelProgressRef.current = {}; setSuccessMessage('');
      setError('');
      try {
        const formRes = await axios.get(`${API_URL}/ems/forms/${targetFormId}`);
        const userRes = await axios.get(`${API_URL}/persons/${personId}`);
        const schemaHasCaddie = formSchemaHasCaddieSelect(
          formRes.data?.schema?.components
        );
        hasCaddieSelectRef.current = schemaHasCaddie;
        const caddies = schemaHasCaddie
          ? await fetchCaddieOptions(API_URL)
          : [];
        const formData = formRes.data;
        setFormNameClass(formNameToCssClass(formData?.form_name));
        const personData = userRes.data?.person || {};
        personRecordRef.current = personData;

        const participantRes = await axios.get(
          `${API_URL}/ems/participants`,
          { params: { personId } }
        );
        const userParticipants = participantRes.data || [];

        // Find the athlete record for data mapping purposes
        const athleteParticipantRecord = userParticipants.find(record =>
          record?.group_id && String(record.group_id?._id) === ATHLETE_GROUP_ID
        );

        const recordToSubmit =
          athleteParticipantRecord ||
          (userParticipants.length > 0 ? userParticipants[0] : null);

        setPortalSummary({ participant: recordToSubmit, person: personData });

        if (recordToSubmit?._id) {
  setParticipantRecordId(recordToSubmit._id);
}

         else {
          console.warn(`User ${personId} has no participant records.`);
        }

        if (!formData?.schema?.components) {
          setError('Form schema is invalid or missing components.');
          setLoading(false);
          return;
        }

        // Prefer the participant record's group (admin dropdown) so user portal matches EMS edits
        const activeGroup = recordToSubmit?.group_id;
        const groupIDForSubmission =
          (activeGroup && typeof activeGroup === 'object' ? activeGroup._id : activeGroup) ||
          athleteParticipantRecord?.group_id?._id ||
          '';
        const groupNameForSubmission =
          (activeGroup && typeof activeGroup === 'object' ? activeGroup.group_name : '') ||
          athleteParticipantRecord?.group_id?.group_name ||
          '';

        const initialSchema = structuredClone(formData.schema);

        if (normalizedSegment === 'travel') {
          traverseComponents(initialSchema.components, (component) => {
            if (component.type === 'panel' && component.key === 'panel') {
              component.title = 'Travel Details';
              component.label = 'Travel Details';
            }
          });
        }

        // Prototype hides datagrid thead — Form.io only renders cell labels when dataGridLabel is set.
        if (
          normalizedSegment === 'accommodation' ||
          normalizedSegment === 'guest' ||
          normalizedSegment === 'travel'
        ) {
          ensureDatagridCellLabels(initialSchema.components);
        }

        stripRequiredUI(initialSchema.components);
        overrideSubmitSettings(initialSchema.components);
        ensureDatagridRowActions(initialSchema.components);
        ensureSafePhoneGroupCalculate(initialSchema.components);
        sanitizeFormioSelectDefaults(initialSchema.components);
        if (normalizedSegment === 'accreditation') {
          configureAccreditationFormFileUploads(initialSchema.components, API_URL);
        }
        if (schemaHasCaddie) {
          caddiesByIdRef.current = buildCaddiesById(caddies);
          ensureCaddieDatagridLayoutFromSchema(initialSchema.components);
        } else {
          caddiesByIdRef.current = new Map();
          hasCaddieSelectRef.current = false;
        }
        originalFormSchemaRef.current = initialSchema;

        const savedPersonData = ensurePlainObject(recordToSubmit?.person_data || {});
        personDataBaselineRef.current = savedPersonData;

        let initialFormData = buildInitialFormData({
          savedPersonData,
          schemaComponents: initialSchema.components,
          groupId: groupIDForSubmission,
          groupName: groupNameForSubmission,
          personRecord: personData,
        });
        sanitizeSelectSubmissionValues(initialFormData);

        // Legacy / guest-shaped phone container on Registration → phoneGroup2.
        if (
          normalizedSegment === 'registration' &&
          initialFormData.phoneNumber &&
          typeof initialFormData.phoneNumber === 'object' &&
          !Array.isArray(initialFormData.phoneNumber)
        ) {
          if (!initialFormData.phoneGroup2) {
            const phoneSrc = initialFormData.phoneNumber;
            initialFormData.phoneGroup2 = {
              country: phoneSrc.country || undefined,
              phoneNumber1: phoneSrc.phoneNumber1 || formatPhoneDisplay(phoneSrc) || '',
            };
          }
          delete initialFormData.phoneNumber;
        }

        // Guest list → Personal Registration: load that guest's details into the same forms.
        if (isEditingGuest && recordToSubmit?._id) {
          try {
            const guestRes = await axios.get(
              `${API_URL}/ems/participants/${editingGuestParticipantId}/guests/${editingGuestId}`
            );
            const guestDoc = guestRes.data;
            guestRecordRef.current = guestDoc;
            guestFormDataRef.current =
              guestDoc?.form_data && typeof guestDoc.form_data === 'object'
                ? { ...guestDoc.form_data }
                : {};
            initialFormData = buildGuestDataForPersonalForm(
              guestDoc,
              normalizedSegment,
              groupIDForSubmission,
              groupNameForSubmission
            );
            sanitizeSelectSubmissionValues(initialFormData);
            personDataBaselineRef.current = { ...initialFormData };
            setPortalSummary({
              participant: {
                ...(recordToSubmit || {}),
                registration_status: 'Guest',
                approval_status: recordToSubmit?.approval_status || 'Pending',
                person_data: {
                  givenFirstName: guestDoc?.firstName || '',
                  familyLastName: guestDoc?.lastName || '',
                  emailAddress: guestDoc?.email || '',
                  ...ensurePlainObject(guestDoc?.meta),
                  ...initialFormData,
                },
              },
              person: {
                name: guestDoc?.firstName || '',
                lname: guestDoc?.lastName || '',
                email: guestDoc?.email || '',
              },
            });
          } catch (err) {
            console.warn('Failed to load guest for Personal Registration', err);
            setError(err?.response?.data?.message || 'Failed to load guest details');
          }
        } else {
          guestRecordRef.current = null;
          guestFormDataRef.current = {};
        }

        // Only probe photo URLs on accreditation — elsewhere this can hang the whole form load.
        if (normalizedSegment === 'accreditation' && initialFormData.photo != null) {
          initialFormData.photo = await ensureAccreditationPhotoDisplay(
            initialFormData.photo,
            API_URL
          );
        }

        if (schemaHasCaddie) {
          // Include DB knownCaddies in the option map so dropdown + row restore share one source.
          const mergedCaddies = mergeKnownCaddiesIntoOptionsList(caddies, initialFormData);
          caddiesByIdRef.current = buildCaddiesById(mergedCaddies);
          injectCaddieSelectValues(
            initialSchema.components,
            mergedCaddies,
            initialFormData
          );
        }

        if (normalizedSegment === 'guest' && recordToSubmit?._id && !isEditingGuest) {
          try {
            const guestsRes = await axios.get(
              `${API_URL}/ems/participants/${recordToSubmit._id}/guests`
            );
            const guestDocs = guestsRes.data || [];
            const guestRows = guestDocs.map((g) => {
              const base =
                g.meta && typeof g.meta === 'object' && Object.keys(g.meta).length
                  ? { ...g.meta }
                  : {
                      firstName: g.firstName || '',
                      lastName: g.lastName || '',
                      fullName: g.firstName || '',
                      email: g.email || '',
                    };
              // Guest form phoneNumber is a container, not a bare string.
              if (!base.phoneNumber || typeof base.phoneNumber === 'string') {
                const digits = formatPhoneDisplay(base.phoneNumber) || formatPhoneDisplay(g.phone_number);
                if (digits) {
                  base.phoneNumber = { phoneNumber1: digits, fullPhoneNumber: digits };
                } else if (typeof base.phoneNumber === 'string') {
                  delete base.phoneNumber;
                }
              }
              if (g._id) base.participant_guest_id = g._id;
              if (g.arrival_departure_periods?.length) {
                base.arrival_departure_periods = g.arrival_departure_periods;
              }
              if (g.stay_periods?.length) base.stay_periods = g.stay_periods;
              return base;
            });
            // Form schemas may bind the grid at top-level dataGrid or under guestDetails.
            initialFormData.dataGrid = guestRows;
            initialFormData.guestDetails = {
              ...(initialFormData.guestDetails && typeof initialFormData.guestDetails === 'object'
                ? initialFormData.guestDetails
                : {}),
              dataGrid: guestRows,
            };
          } catch (err) {
            console.warn('Failed to load guests from API, falling back to empty grid', err);
          }
        }

        const { schema: finalSchema, globalProgressStats } =
          updateProgressStats(initialFormData, initialSchema.components, true); // initialLoad = true

        if (finalSchema) {
          setFormSchema(finalSchema);
          let safeInitialData = normalizeDataGridForFormioRender(
            structuredClone(initialFormData || {})
          );
          if (schemaHasCaddie && caddiesByIdRef.current?.size) {
            safeInitialData = prepareCaddieGridDisplayData(
              safeInitialData,
              caddiesByIdRef.current,
              null,
              { syncFormio: false }
            );
          }
          setInitialSubmissionData({ data: safeInitialData });
          setSubmissionData(safeInitialData); // Update submissionData state immediately for isHiddenForAthlete check
          await Promise.resolve();
          if (normalizedSegment && globalProgressStats) {
            updateLocalPanelProgress(normalizedSegment, {
              total: globalProgressStats.total,
              completed: globalProgressStats.filled,
              display: `${globalProgressStats.filled}/${globalProgressStats.total}`,
            });
          }
          // Required fields already filled in DB → allow tab switching.
          const alreadyComplete = areRequiredFieldsFilled(
            initialFormData,
            initialSchema.components,
            null
          );
          setFormFullySaved(alreadyComplete);
          setHasUnsavedEdits(false);
          hasUnsavedEditsRef.current = false;
          allowDirtyTrackingRef.current = false;
        }
      } catch (err) {
        console.error('Error fetching:', err.response || err.message || err);
        const status = err.response?.status;
        const apiMessage = err.response?.data?.message || err.response?.data?.msg;
        if (status === 401) {
          setError(
            'Your session is missing or expired. Please log out and sign in again at User Login.'
          );
        } else {
          setError(
            apiMessage
              ? `Failed to fetch form or user data: ${apiMessage}`
              : status
                ? `Failed to fetch form or user data (HTTP ${status}).`
                : 'Failed to fetch form or user data. Check that the API server is running.'
          );
        }
        originalFormSchemaRef.current = null;
        setFormSchema(null);
        setInitialSubmissionData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchFormAndUser();
  }, [
    API_URL,
    targetFormId,
    personId,
    updateProgressStats,
    normalizedSegment,
    isEditingGuest,
    editingGuestId,
    editingGuestParticipantId,
  ]);

  // Guest-edit mode uses Personal Registration tabs only (no Guest datagrid tab).
  useEffect(() => {
    if (!isEditingGuest || normalizedSegment !== 'guest') return;
    navigate(formViewPath('registration', FORM_MAPPING.registration), {
      replace: true,
    });
  }, [
    isEditingGuest,
    normalizedSegment,
    navigate,
    editingGuestId,
    editingGuestParticipantId,
  ]); 

  const markFormDirty = () => {
    if (!allowDirtyTrackingRef.current) return;
    if (!hasUnsavedEditsRef.current) {
      hasUnsavedEditsRef.current = true;
      setHasUnsavedEdits(true);
    }
    setFormFullySaved(false);
    setTabGuardMessage('');
  };

  const capturePristineAndEnableDirty = (data) => {
    try {
      pristineDataRef.current = JSON.stringify(data || {});
    } catch {
      pristineDataRef.current = null;
    }
    allowDirtyTrackingRef.current = true;
  };

  const getLiveFormData = () => {
    const webform = resolveFormioWebform(
      formioWebformRef.current,
      reactFormRef.current,
      formRef.current
    );
    return (
      webform?.submission?.data ||
      submissionData ||
      initialSubmissionData?.data ||
      {}
    );
  };

  /** True if user changed fields since pristine snapshot (or dirty flag already set). */
  const isFormDirtyNow = () => {
    // Prefer explicit dirty flag from real edits. Avoid JSON≠pristine alone —
    // Form.io/Choices mutate submission after load without user input.
    return Boolean(hasUnsavedEditsRef.current || hasUnsavedEdits);
  };

  /**
   * Arm mid-edit tracking after hydrate.
   * Must NOT rely only on react-formio `onReady` — that event often never fires.
   */
  const armDirtyTracking = () => {
    const webform = resolveFormioWebform(
      formioWebformRef.current,
      reactFormRef.current,
      formRef.current
    );
    // onReady often missing — still capture formio from react-formio ref after mount.
    if (webform) {
      formioWebformRef.current = webform;
      formRef.current = webform;
      if (reactFormRef.current) reactFormRef.current.formio = webform;
    }
    const live =
      webform?.submission?.data ||
      initialSubmissionData?.data ||
      {};
    capturePristineAndEnableDirty(live);

    if (dirtyDomCleanupRef.current) {
      dirtyDomCleanupRef.current();
      dirtyDomCleanupRef.current = null;
    }

    const onWebformChange = (event) => {
      if (!allowDirtyTrackingRef.current) return;
      if (event?.changed && !event.changed?.flags?.fromSubmission) {
        markFormDirty();
      }
    };
    if (webform && typeof webform.on === 'function') {
      webform.on('change', onWebformChange);
    }

    const wrapper = formWrapperRef.current;
    const onDomEdit = (e) => {
      if (!allowDirtyTrackingRef.current) return;
      const t = e?.target;
      if (!t) return;
      if (t.closest?.('.user-form-tabs, .modal, .user-form-leave-dialog')) return;
      const tag = String(t.tagName || '').toLowerCase();
      if (
        tag === 'input' ||
        tag === 'select' ||
        tag === 'textarea' ||
        t.isContentEditable
      ) {
        markFormDirty();
      }
    };
    if (wrapper) {
      wrapper.addEventListener('input', onDomEdit, true);
      wrapper.addEventListener('change', onDomEdit, true);
    }

    dirtyDomCleanupRef.current = () => {
      if (webform && typeof webform.off === 'function') {
        webform.off('change', onWebformChange);
      }
      if (wrapper) {
        wrapper.removeEventListener('input', onDomEdit, true);
        wrapper.removeEventListener('change', onDomEdit, true);
      }
    };
  };

  // Primary dirty arm — independent of onReady (react-formio often skips it).
  useEffect(() => {
    if (loading || !formSchema || !initialSubmissionData) return undefined;

    hasUnsavedEditsRef.current = false;
    setHasUnsavedEdits(false);
    allowDirtyTrackingRef.current = false;
    pristineDataRef.current = null;

    if (dirtyEnableTimerRef.current) {
      clearTimeout(dirtyEnableTimerRef.current);
      dirtyEnableTimerRef.current = null;
    }
    if (dirtyDomCleanupRef.current) {
      dirtyDomCleanupRef.current();
      dirtyDomCleanupRef.current = null;
    }

    dirtyEnableTimerRef.current = window.setTimeout(() => {
      armDirtyTracking();
      // Re-snapshot after Choices / calculated fields settle so we don't start dirty.
      window.setTimeout(() => {
        if (!hasUnsavedEditsRef.current) {
          capturePristineAndEnableDirty(getLiveFormData());
        }
      }, 900);
      dirtyEnableTimerRef.current = null;
    }, 700);

    return () => {
      if (dirtyEnableTimerRef.current) {
        clearTimeout(dirtyEnableTimerRef.current);
        dirtyEnableTimerRef.current = null;
      }
      if (dirtyDomCleanupRef.current) {
        dirtyDomCleanupRef.current();
        dirtyDomCleanupRef.current = null;
      }
    };
    // Re-arm only when the form instance itself changes — not on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, formSchema, initialSubmissionData, normalizedSegment, photoKey]);

  /**
   * ✅ FIX: Removed setFormProgressData call. Panel badges still update locally.
   */
  const handleFormChange = (submission, flags) => {
    try {
      // Only real field edits mark dirty — not Form.io hydrate/calc data drift
      // (JSON≠pristine after Choices init was making Unsaved modal always appear).
      if (allowDirtyTrackingRef.current) {
        const changedComp = submission?.changed;
        const fromSubmission = Boolean(changedComp?.flags?.fromSubmission);
        const flagged =
          (Boolean(changedComp) && !fromSubmission) ||
          flags?.modified === true ||
          flags?.isModified === true;
        if (flagged) markFormDirty();
      }

      const newSubmissionData = { ...(submission.data || {}) };

      if (hasCaddieSelectRef.current && submission.changed) {
        const beforeJson = JSON.stringify(submission.data || {});
        const formioInstance = resolveFormioWebform(
          formioWebformRef.current,
          reactFormRef.current,
          formRef.current
        );
        const synced = applyCaddieOnFormChange(
          submission,
          formioInstance,
          caddiesByIdRef.current
        );
        Object.assign(newSubmissionData, synced);

        const radioChange = isDefaultRadioFormChange(submission.changed);
        const caddieDataChanged = JSON.stringify(synced) !== beforeJson;
        if (radioChange || caddieDataChanged) {
          if (formioInstance) {
            pushFormioSubmissionData(formioInstance, synced, {
              gridOnly: radioChange,
              radioOnly: radioChange,
            });
          } else if (radioChange && formWrapperRef.current) {
            enforceExclusiveDefaultRadioDom(
              formWrapperRef.current,
              synced,
              getCaddieDatagridLayout()
            );
          }
          setSubmissionData(newSubmissionData);
          if (radioChange && Array.isArray(synced.presentCaddies)) {
            personDataBaselineRef.current = {
              ...personDataBaselineRef.current,
              presentCaddies: synced.presentCaddies,
              dataGrid: synced.dataGrid,
              knownCaddies: synced.knownCaddies,
            };
          }
          // Do not remount Form.io on caddie select — remount races forceFill
          // and can wipe Caddie Email. Live instance is updated via pushFormio.
          if (originalFormSchemaRef.current) {
            const { globalProgressStats } = updateProgressStats(
              newSubmissionData,
              originalFormSchemaRef.current.components,
              false
            );
            if (normalizedSegment && globalProgressStats) {
              updateLocalPanelProgress(normalizedSegment, {
                total: globalProgressStats.total,
                completed: globalProgressStats.filled,
                display: `${globalProgressStats.filled}/${globalProgressStats.total}`,
              });
            }
          }
          setSuccessMessage('');
          return;
        }
      }

      setSubmissionData(newSubmissionData);

      if (originalFormSchemaRef.current) {
        const { globalProgressStats } = updateProgressStats(
          newSubmissionData,
          originalFormSchemaRef.current.components,
          false
        );

        if (normalizedSegment && submission.changed && globalProgressStats) {
          updateLocalPanelProgress(normalizedSegment, {
            total: globalProgressStats.total,
            completed: globalProgressStats.filled,
            display: `${globalProgressStats.filled}/${globalProgressStats.total}`,
          });
        }
      }

      setSuccessMessage('');
    } catch (err) {
      console.error('handleFormChange error:', err);
    }
  };





  const handleTabSelect = (tab) => {
    if (!tab || tab.key === normalizedSegment) return;

    // Mid-edit tab switch → Unsaved changes modal.
    // Live data compare covers cases where dirty flag never stuck.
    if (isFormDirtyNow()) {
      const currentLabel =
        FORM_TABS.find((item) => item.key === normalizedSegment)?.label ||
        'this form';
      setLeaveTabConfirm({ tab, currentLabel });
      return;
    }

    setTabGuardMessage('');
    navigate(formViewPath(tab.key, tab.id));
  };

  const handleLeaveTabDismiss = () => {
    if (leaveTabSaving) return;
    pendingNavigateTabRef.current = null;
    setLeaveTabConfirm(null);
  };

  const handleLeaveTabConfirm = () => {
    if (leaveTabSaving) return;
    const tab = leaveTabConfirm?.tab;
    pendingNavigateTabRef.current = null;
    setLeaveTabConfirm(null);
    if (!tab) return;
    setTabGuardMessage('');
    navigate(formViewPath(tab.key, tab.id));
  };

  const triggerPortalFormSave = () => {
    const webform = formioWebformRef.current || formRef.current;
    if (webform && typeof webform.submit === 'function') {
      webform.submit();
      return true;
    }
    const submitBtn = formWrapperRef.current?.querySelector(
      'button[type="submit"], .formio-component-button button, button.btn-primary'
    );
    if (submitBtn) {
      submitBtn.click();
      return true;
    }
    return false;
  };

  const handleLeaveTabSaveAndNext = () => {
    const tab = leaveTabConfirm?.tab;
    if (!tab || leaveTabSaving) return;

    pendingNavigateTabRef.current = tab;
    setLeaveTabSaving(true);
    setSuccessMessage('Saving…');

    window.requestAnimationFrame(() => {
      const started = triggerPortalFormSave();
      if (!started) {
        pendingNavigateTabRef.current = null;
        setLeaveTabSaving(false);
        setSuccessMessage('❌ Could not start save. Please use Save & Continue.');
        setTimeout(() => setSuccessMessage(''), 4000);
      }
    });
  };

  const takePendingNavigateTab = () => {
    const pending = pendingNavigateTabRef.current;
    pendingNavigateTabRef.current = null;
    setLeaveTabSaving(false);
    setLeaveTabConfirm(null);
    return pending;
  };

  const handleFormReady = (formio) => {
    try {
      const webform = resolveFormioWebform(formio, reactFormRef.current) || formio;
      formioWebformRef.current = webform;
      formRef.current = webform;
      if (reactFormRef.current) {
        reactFormRef.current.formio = webform;
      }
      // Local API save only — never POST to Form.io cloud (keeps submit path deterministic).
      webform.nosubmit = true;

      // ⭐️ ATTACH CONFIRMATION FOR GUEST DELETE
      attachDeleteConfirmation(formio, normalizedSegment);

      // Recalc with live Form.io visibility (excludes conditionally hidden fields).
      if (originalFormSchemaRef.current) {
        const liveData =
          webform.submission?.data ||
          initialSubmissionData?.data ||
          submissionData ||
          {};
        const { globalProgressStats } = updateProgressStats(
          liveData,
          originalFormSchemaRef.current.components,
          false
        );
        if (normalizedSegment && globalProgressStats) {
          updateLocalPanelProgress(normalizedSegment, {
            total: globalProgressStats.total,
            completed: globalProgressStats.filled,
            display: `${globalProgressStats.filled}/${globalProgressStats.total}`,
          });
        }
      } else {
        updateFormioBadges(formRef.current, panelProgressRef.current);
      }

      if (hasCaddieSelectRef.current) {
        const caddieList = Array.from(caddiesByIdRef.current.values());
        let base =
          formio.submission?.data ||
          initialSubmissionData?.data ||
          {};
        try {
          base = normalizeDataGridForFormioRender(JSON.parse(JSON.stringify(base)));
        } catch {
          base = normalizeDataGridForFormioRender({ ...base });
        }
        // Always reinject — even with an empty CRM list — so URL dataSrc is
        // forced to values and the dropdown does not hang on click.
        reinjectCaddieOptionsOnFormio(webform, caddieList, base);
        attachCaddieEmailAutoFill(webform, () => caddiesByIdRef.current);
        if (caddiesByIdRef.current.size) {
          const synced = syncCaddieFieldsForFormio(
            webform,
            base,
            caddiesByIdRef.current
          );
          if (synced) {
            setSubmissionData(synced);
            if (formWrapperRef.current) {
              enforceExclusiveDefaultRadioDom(
                formWrapperRef.current,
                synced,
                getCaddieDatagridLayout()
              );
            }
          }
        }
      }
      // Dirty arm owned by useEffect (onReady often missing in react-formio).
      // If not armed yet and formio just became available, arm now.
      if (!allowDirtyTrackingRef.current && !dirtyEnableTimerRef.current) {
        armDirtyTracking();
      }
    } catch (err) {
      console.error('handleFormReady error:', err);
    }
  };


  /**
   * ✅ FIX: Removed setFormProgressData call. Form data is still saved to DB.
   */
   const handleFormSubmit = async (submission) => {
    if (!participantRecordId) {
      setSuccessMessage('Cannot save: No valid participant record ID.');
      setTimeout(() => setSuccessMessage(''), 3000);
      clearFormioSubmitLoading(
        resolveFormioWebform(formioWebformRef.current, reactFormRef.current, formRef.current)
      );
      return Promise.resolve(submission);
    }
    if (isSubmittingRef.current) {
      return Promise.resolve(submission);
    }
    isSubmittingRef.current = true;

    const formioInstance = resolveFormioWebform(
      formioWebformRef.current,
      reactFormRef.current,
      formRef.current
    );
    if (formioInstance) formioInstance.nosubmit = true;
    const submitData = formioInstance
      ? snapshotFormioInstanceData(formioInstance)
      : ensurePlainObject(submission.data);
    const isRegCaddie =
      normalizedSegment === 'registration' && hasCaddieSelectRef.current;
    let displayData = submitData;

    try {
    // 1. Sync default caddie radio on the live Form.io instance (no React submission remount).
    if (isRegCaddie) {
      displayData = prepareCaddieGridDisplayData(
        submitData,
        caddiesByIdRef.current,
        formioInstance,
        { forSubmit: true, syncFormio: false }
      );
    }

    // 2. Update panel progress locally and capture the latest field counts
    const progressResult = originalFormSchemaRef.current
      ? updateProgressStats(displayData, originalFormSchemaRef.current.components, false)
      : {
          globalProgressStats: { percentage: 0, filled: 0, total: 0 },
        };
    const { globalProgressStats } = progressResult;
    const fieldTotal = globalProgressStats?.total || 0;
    const fieldFilled = globalProgressStats?.filled || 0;
    // Form.io only fires onSubmit after required validation passes, so Save & Continue
    // always unlocks tabs and advances (optional empty fields must not block).
    const formIsFullyFilled = true;

    // 3. Update panel badges in-place — avoid setFormSchema on submit (it remounts and clears caddie radio).
    if (formRef.current) {
      updateFormioBadges(formRef.current, panelProgressRef.current);
    }


    // 4. ⭐️ FIX: Update the GLOBAL Panel Data Service
    // This immediately notifies the sidebar/navigation component to refresh its badges.
    if (participantRecordId && normalizedSegment && !isEditingGuest) {
      setFormProgressData(participantRecordId, normalizedSegment, {
        total: fieldTotal,
        completed: fieldFilled,
        display: `${fieldFilled}/${fieldTotal}`,
      });
    }


    // 5. Show "Saving..."
    setSuccessMessage('Saving...');

    // 6. Save asynchronously to DB
    try {
      // progress_breakdown must use form segment keys (registration, guest, …),
      // not Form.io panel keys (personalInformation, …). Field filled/total.
      const mappedProgress = {
        [normalizedSegment]: {
          total: fieldTotal,
          completed: fieldFilled,
          display: `${fieldFilled}/${fieldTotal}`,
        },
      };

      let personDataForParticipant = mergeWith(
        {},
        personDataBaselineRef.current,
        displayData,
        (_dest, src) => (Array.isArray(src) ? cloneDeep(src) : undefined)
      );
      personDataForParticipant.Group_id =
        displayData?.Group_id || personDataForParticipant.Group_id || '';
      personDataForParticipant.groupName =
        displayData?.groupName || personDataForParticipant.groupName || '';

      if (originalFormSchemaRef.current?.components) {
        personDataForParticipant = filterPersonDataForFormSave(
          personDataForParticipant,
          originalFormSchemaRef.current.components,
          personDataBaselineRef.current
        );
      }

      const baselineHasCaddie = personDataHasCaddieFields(personDataBaselineRef.current);

      // Editing a guest: save into participant_guests.form_data, not host person_data.
      if (isEditingGuest) {
        const segmentKey = String(normalizedSegment || '').toUpperCase();
        const existing = guestFormDataRef.current || {};
        const tabPayload = { ...displayData };
        delete tabPayload.Group_id;
        delete tabPayload.groupName;
        const mergedFormData = {
          ...existing,
          [segmentKey]: {
            ...(existing[segmentKey] || {}),
            ...tabPayload,
          },
        };
        const guestRes = await axios.put(
          `${API_URL}/ems/participants/${editingGuestParticipantId}/guests/${editingGuestId}`,
          { form_data: mergedFormData }
        );
        guestRecordRef.current = guestRes.data;
        guestFormDataRef.current = guestRes.data?.form_data || mergedFormData;
        personDataBaselineRef.current = { ...displayData };
        setShowInlineErrors(false);
        setPortalSummary((current) => ({
          ...current,
          person: {
            name:
              displayData.givenFirstName ||
              guestRes.data?.firstName ||
              current.person?.name ||
              '',
            lname:
              displayData.familyLastName ||
              guestRes.data?.lastName ||
              current.person?.lname ||
              '',
            email:
              displayData.emailAddress ||
              guestRes.data?.email ||
              current.person?.email ||
              '',
          },
          participant: current.participant
            ? {
                ...current.participant,
                person_data: {
                  ...(current.participant.person_data || {}),
                  ...displayData,
                },
              }
            : current.participant,
        }));
        setFormFullySaved(formIsFullyFilled);
        setHasUnsavedEdits(false);
        hasUnsavedEditsRef.current = false;
        capturePristineAndEnableDirty(displayData);
        const pendingTab = takePendingNavigateTab();
        const nextTab =
          pendingTab || getNextFormTab(normalizedSegment, isAthlete, true);
        if (!nextTab) {
          setSuccessMessage('Guest form saved successfully!');
          setTimeout(() => setSuccessMessage(''), 2500);
        } else {
          setSuccessMessage('Saved — opening next form…');
          navigate(formViewPath(nextTab.key, nextTab.id));
        }
        return Promise.resolve(submission);
      }

      if (normalizedSegment === 'guest') {
        const guestRows =
          (Array.isArray(submission.data?.dataGrid) && submission.data.dataGrid) ||
          (submission.data?.guestDetails && Array.isArray(submission.data.guestDetails.dataGrid) && submission.data.guestDetails.dataGrid) ||
          (submission.data?.guest && Array.isArray(submission.data.guest.dataGrid) && submission.data.guest.dataGrid) ||
          [];
        await axios.put(`${API_URL}/ems/participants/${participantRecordId}/guests`, guestRows);
        personDataForParticipant = { ...personDataForParticipant };
        delete personDataForParticipant.guestDetails;
        delete personDataForParticipant.guest;
        if (baselineHasCaddie) {
          copyCaddieFieldsFromSource(
            personDataForParticipant,
            personDataBaselineRef.current
          );
        } else {
          delete personDataForParticipant.dataGrid;
        }
      }

      if (hasCaddieSelectRef.current || baselineHasCaddie) {
        personDataForParticipant = preserveAndNormalizeCaddiesOnSave(
          personDataForParticipant,
          personDataBaselineRef.current,
          {
            caddiesById: caddiesByIdRef.current,
            formio: formioInstance,
            isRegistrationTab: isRegCaddie,
            liveSnapshot: displayData,
            tabLabel: normalizedSegment,
          }
        );
      }

      preserveParticipantIdentityFromPerson(
        personDataForParticipant,
        personRecordRef.current
      );

      const payload = {
        person_data: personDataForParticipant,
        progress_breakdown: mappedProgress,
      };
      await axios.put(`${API_URL}/ems/participants/${participantRecordId}`, payload);
      personDataBaselineRef.current = personDataForParticipant;
      setShowInlineErrors(false);
      setPortalSummary((current) => ({
        ...current,
        participant: current.participant
          ? { ...current.participant, person_data: personDataForParticipant }
          : current.participant,
      }));

      setFormFullySaved(formIsFullyFilled);
      setHasUnsavedEdits(false);
      hasUnsavedEditsRef.current = false;
      capturePristineAndEnableDirty(displayData);

      const pendingTab = takePendingNavigateTab();
      const nextTab =
        pendingTab || getNextFormTab(normalizedSegment, isAthlete, isEditingGuest);
      const leaveForNextTab = Boolean(nextTab && formIsFullyFilled);

      // Only remount caddie grid when staying on this form (navigating away remounts anyway).
      if (
        !leaveForNextTab &&
        (hasCaddieSelectRef.current || personDataHasCaddieFields(personDataForParticipant))
      ) {
        displayData = normalizeDataGridForFormioRender(
          JSON.parse(JSON.stringify(personDataForParticipant))
        );
        setSubmissionData(displayData);
        setInitialSubmissionData({ data: cloneDeep(displayData) });
        if (formioInstance) {
          pushFormioSubmissionData(formioInstance, displayData, { gridOnly: true });
        }
      }

      if (!nextTab) {
        setSuccessMessage('Form data and progress saved successfully!');
        setTimeout(() => setSuccessMessage(''), 2500);
      } else {
        setSuccessMessage('Saved — opening next form…');
        navigate(formViewPath(nextTab.key, nextTab.id));
      }
    } catch (err) {
      console.error('Error submitting:', err.message || err);
      pendingNavigateTabRef.current = null;
      setLeaveTabSaving(false);
      const apiMsg = err.response?.data?.message;
      setSuccessMessage(apiMsg ? `❌ ${apiMsg}` : '❌ Failed to save form data and progress.');
      setTimeout(() => setSuccessMessage(''), 4000);
    }

    } catch (outerErr) {
      console.error('Unexpected error in handleFormSubmit:', outerErr);
      pendingNavigateTabRef.current = null;
      setLeaveTabSaving(false);
      setSuccessMessage('❌ An unexpected error occurred. Please try again.');
      setTimeout(() => setSuccessMessage(''), 4000);
    } finally {
      isSubmittingRef.current = false;
      clearFormioSubmitLoading(formioInstance);
    }

    if (isRegCaddie && displayData) {
      return Promise.resolve({ ...submission, data: cloneDeep(displayData) });
    }
    return Promise.resolve(submission);
  };

  // Load all tab progress from DB once per participant. Do NOT reload on tab switch —
  // that wiped live filled/total for other forms. Live tab updates via onChange/onReady/save.
  useEffect(() => {
    if (!participantRecordId || isHiddenForAthlete) return;

    let cancelled = false;
    (async () => {
      await loadSavedProgress(participantRecordId);
      if (cancelled) return;
    })();

    return () => {
      cancelled = true;
    };
  }, [participantRecordId, isHiddenForAthlete]);

  // Refresh only the open tab from live form data after schema/data are ready.
  useEffect(() => {
    if (!participantRecordId || !normalizedSegment || isHiddenForAthlete) return;

    const schemaComponents = originalFormSchemaRef.current?.components;
    const liveData =
      formRef.current?.submission?.data ||
      submissionData ||
      initialSubmissionData?.data;
    if (!schemaComponents || !liveData) return;

    const { globalProgressStats } = updateProgressStats(
      liveData,
      schemaComponents,
      false
    );
    if (globalProgressStats) {
      updateLocalPanelProgress(normalizedSegment, {
        total: globalProgressStats.total,
        completed: globalProgressStats.filled,
        display: `${globalProgressStats.filled}/${globalProgressStats.total}`,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    participantRecordId,
    normalizedSegment,
    isHiddenForAthlete,
    loading,
    initialSubmissionData,
  ]);


// --- Photo update callback (accreditation crop) ---

  const handlePhotoUpdated = (newFullUrl) => {
    setInitialSubmissionData(prev => {
      if (!prev) return prev;
      // Preserve original_url (and any other fields) from the existing photo entry
      // so the "Original vs Cropped" card keeps showing the original.
      const existing =
        Array.isArray(prev.data?.photo) &&
        prev.data.photo[0] &&
        typeof prev.data.photo[0] === 'object'
          ? prev.data.photo[0]
          : {};
      const newPhoto = [{
        ...existing,
        url: newFullUrl,
        name: String(newFullUrl).split('/').pop() || 'photo.png',
        type: 'image/png',
        storage: 'url',
      }];
      return { ...prev, data: { ...prev.data, photo: newPhoto } };
    });
    setPhotoKey(k => k + 1);
  };

  /* Watch the form wrapper for Formio's photo field and store it as a portal target. */
  useEffect(() => {
    if (normalizedSegment !== 'accreditation') { setPhotoFieldPortalEl(null); return; }
    const container = formWrapperRef.current;
    if (!container) return;
    const find = () => {
      const img = container.querySelector('.formio-component-photo img');
      setPhotoFieldPortalEl(img?.parentElement || null);
    };
    find();
    const obs = new MutationObserver(find);
    obs.observe(container, { childList: true, subtree: true });
    return () => { obs.disconnect(); setPhotoFieldPortalEl(null); };
  // `loading` is included so the observer re-runs once the form div is mounted.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedSegment, photoKey, loading]);

// --- RENDER LOGIC ---

  // 🛑 FINAL FIX: Check the computed flag and display the access denied message.
  if (!loading && isHiddenForAthlete) {
    return (
      <div style={{ padding: "20px", color: "#856404", backgroundColor: "#fff3cd", border: "1px solid #ffeeba", borderRadius: "5px", fontSize: "18px", fontWeight: "bold" }}>
        Access Denied – This form is not required for the Athlete category.
      </div>
    );
  }

  const MessageDisplay = ({ error, success }) => {
    if (error) return (
      <div className="alert alert-danger" role="alert">
        <strong>Error</strong>
        <div>{error}</div>
      </div>
    );
    if (success) return (
      <div className="alert alert-info position-fixed top-0 start-50 translate-middle-x mt-3 shadow" style={{zIndex: 1050}}>
        {success}
      </div>
    );
    return null;
  };

  const hasShell =
    Boolean(portalSummary?.participant || portalSummary?.person) ||
    Boolean(!loading && formSchema);

  // First paint only — keep header visible during tab switches for smooth transition
  if (loading && !hasShell) {
    return (
      <div className="user-form-loading" aria-busy="true" aria-label="Loading form">
        <Spinner animation="border" role="status" style={{ color: '#d9a128', width: 36, height: 36 }} />
        <span className="user-form-loading__text">Loading form…</span>
      </div>
    );
  }

  if (!loading && error && !formSchema) {
    return (
      <div className="p-4">
        <MessageDisplay error={error} />
      </div>
    );
  }

  const { currentPhotoUrl: currentAccreditationPhotoUrl, originalPhotoUrl: originalAccreditationPhotoUrl } =
    !loading && normalizedSegment === 'accreditation' && initialSubmissionData
      ? getAccreditationPhotoDisplayUrls(initialSubmissionData?.data, API_URL)
      : { currentPhotoUrl: null, originalPhotoUrl: null };

  const formReady = !loading && formSchema && initialSubmissionData !== null;

  return (
    <div
      className={`user-layout user-form-prototype${showInlineErrors ? ' user-form--show-errors' : ''}${formNameClass ? ` ${formNameClass}` : ''}`}
      ref={formWrapperRef}
    >
      <MessageDisplay error={error} success={successMessage} />

      <UserFormPrototypeHeader
        activeSegment={normalizedSegment}
        canSwitchTabs={!loading}
        isAthlete={isAthlete}
        hideGuestTab={isEditingGuest}
        onTabSelect={handleTabSelect}
        participant={portalSummary.participant}
        person={portalSummary.person}
      />

      {ReactDOM.createPortal(
        <UnsavedChangesModal
          show={Boolean(leaveTabConfirm)}
          currentLabel={leaveTabConfirm?.currentLabel}
          nextLabel={leaveTabConfirm?.tab?.label}
          saving={leaveTabSaving}
          onDismiss={handleLeaveTabDismiss}
          onLeaveAnyway={handleLeaveTabConfirm}
          onSaveAndNext={handleLeaveTabSaveAndNext}
        />,
        document.body
      )}

      <div className="user-form-scroll">
        {loading && (
          <div className="user-form-loading user-form-loading--panel" aria-busy="true" aria-label="Loading form">
            <Spinner animation="border" role="status" style={{ color: '#d9a128', width: 32, height: 32 }} />
            <span className="user-form-loading__text">Loading form…</span>
          </div>
        )}

        {!loading && !formReady && (
          <p className="user-form-loading__text" style={{ padding: '24px 0' }}>
            No form or user data found for ID: {targetFormId}
          </p>
        )}

        {formReady && (
          <div className="user-form-body-enter" key={`${normalizedSegment}-${photoKey}`}>
        {tabGuardMessage && (
          <div className="user-form-tab-guard" role="status">
            <span className="user-form-tab-guard__icon" aria-hidden="true">!</span>
            <div>
              <strong>Please finish this step first</strong>
              <p>{tabGuardMessage} Then select <strong>Save &amp; Continue</strong>.</p>
            </div>
            <button
              type="button"
              className="user-form-tab-guard__close"
              aria-label="Dismiss message"
              onClick={() => setTabGuardMessage('')}
            >
              ×
            </button>
          </div>
        )}

        {normalizedSegment === 'accreditation' && participantRecordId && (
          <AccreditationPhotoManager
            ref={photoManagerRef}
            headless
            participantId={participantRecordId}
            currentPhotoUrl={currentAccreditationPhotoUrl}
            originalPhotoUrl={originalAccreditationPhotoUrl}
            onPhotoUpdated={handlePhotoUpdated}
          />
        )}

        {/* Portal: buttons below the photo */}
        {normalizedSegment === 'accreditation' && photoFieldPortalEl && ReactDOM.createPortal(
            <div className="d-flex gap-2 mt-2" style={{marginBottom: '10px', marginLeft: '45px'}}>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => photoManagerRef.current?.openView()}
              disabled={!currentAccreditationPhotoUrl}
            >
              View
            </button>
            <button
              type="button"
              className="btn btn-sm"
              style={{ background: '#D9A128', color: '#fff', border: 'none' }}
              onClick={() => photoManagerRef.current?.openCrop()}
              disabled={!currentAccreditationPhotoUrl}
            >
              Edit / Crop
            </button>
          </div>,
          photoFieldPortalEl
        )}

        {showInlineErrors && (
          <div className="user-form-inline-hint" role="status">
            Please complete the highlighted required fields below.
          </div>
        )}

        <Form
              ref={reactFormRef}
              key={`${normalizedSegment}-${photoKey}`}
              form={formSchema}
              submission={initialSubmissionData}
              options={{
                ...USER_PORTAL_FORMIO_OPTIONS,
                noAlerts: true,
                highlightErrors: false,
              }}
              onChange={handleFormChange}
              onComponentChange={() => {
                if (allowDirtyTrackingRef.current) markFormDirty();
              }}
              onSubmit={handleFormSubmit}
              onReady={handleFormReady}
              onInitialized={handleFormReady}
              onError={() => {
                pendingNavigateTabRef.current = null;
                setLeaveTabSaving(false);
                isSubmittingRef.current = false;
                const fi = resolveFormioWebform(
                  formioWebformRef.current,
                  reactFormRef.current,
                  formRef.current
                );
                clearFormioSubmitLoading(fi);
                setShowInlineErrors(true);
                setSuccessMessage(
                  'Please complete the highlighted required fields, then try Save and next again.'
                );
                setTimeout(() => setSuccessMessage(''), 4000);
                /* Same paint path as Participant Edit — Choices/select need force marks. */
                const reapply = () => {
                  if (fi) forceFormioShowValidationErrors(fi);
                  const firstInvalid = formWrapperRef.current?.querySelector(
                    '.formio-component.has-error, .formio-component:has(.is-invalid), .form-control.is-invalid, .choices.is-invalid'
                  );
                  firstInvalid?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                };
                window.requestAnimationFrame(() => {
                  reapply();
                  window.setTimeout(reapply, 50);
                });
              }}
            />
          </div>
        )}
      </div>

    </div>
  );
};

export default React.memo(UserFormView);